import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { resource } from '@util/resource';
import { DWS } from '@interfaces/dws';
import { environment } from 'environments/environment';
import { map } from 'rxjs/operators';
import { StatisticCampaign, SendGridMessagesPage } from '../../interfaces/dws/statistic-campaign';
import { DownloadType } from '@interfaces/dws/statistic-campaign';
import { SendGridMessagesCacheService } from './send-grid-messages-cache.service';

@Injectable({
  providedIn: 'root'
})
export class StatisticsService {
  updated$: Subject<void> = new Subject<void>();

  constructor(private readonly sendGridMessagesCache: SendGridMessagesCacheService) {}

  get(contactId: any, contactEmail: any, wineryId: string): Observable<DWS.Statistic> {
    return resource('crm://statistics')
      .params({contactId, wineryId, contactEmail: encodeURIComponent(contactEmail)})
      .get<DWS.Statistic>();
  }

  getPayments(contactId: any, wineryId: string): Observable<DWS.PaymentStatistic> {
    return resource('crm://statistics/payments')
      .params({ contactId, wineryId })
      .get<DWS.PaymentStatistic>();
  }

  getOrders(contactId: any, wineryId: string): Observable<DWS.StatisticOrder> {
    return resource('crm://statistics/orders')
      .params({ contactId, wineryId })
      .get<DWS.StatisticOrder>();
  }

  getOrdersProducts(contactId: any, wineryId: string): Observable<DWS.StatisticOrderProduct[]> {
    return resource('crm://statistics/orders/products')
      .params({ contactId, wineryId })
      .get<DWS.StatisticOrderProduct[]>();
  }

  getCampaigns(wineryId: string): Observable<DWS.StatisticCampaign[]> {
    let params: any = {
      reference: environment.values['dateSendGridStatistics'],
      wineryId: wineryId,
      archived: true
    };
    return resource('crm://statistics/campaigns')
      .params(params)
      .get<DWS.StatisticCampaign[]>()
      .pipe(
        map((statistics) => {
          if (!statistics || !statistics.length) return [];
          const data: DWS.StatisticCampaign[] = []
          statistics.map((statistic) => {
            const statisticCampaign: DWS.StatisticCampaign = {
              campaignId: statistic.campaignId,
              totalSent: statistic.totalSent,
              totalOpened: statistic.totalOpened,
              openedPercentage:  statistic.totalOpened ? Math.round(100 * statistic.totalOpened/statistic.totalSent): 0,
              lastOpenedAt: statistic.lastOpenedAt ? statistic.lastOpenedAt : '-'
            }
            data.push(statisticCampaign);
          });
          return data;
        })
      );
  }
  
  getCampaignsByIds(wineryId: string, campaigns: DWS.Campaign[]): Observable<DWS.StatisticCampaign[]> {
    let params: any = {
      reference: environment.values['dateSendGridStatistics'],
      wineryId: wineryId,
      ids: campaigns.map(campaign => campaign.id),
      archived: true
    };
    
    if(params.ids.length === 0) {
      return new Observable<DWS.StatisticCampaign[]>(subscriber => subscriber.next([]));
    }

    return resource('crm://statistics/campaigns/multiple')
      .params(params)
      .get<DWS.StatisticCampaign[]>()
      .pipe(
        map((statistics) => {
          if (!statistics || !statistics.length) return [];
          const data: DWS.StatisticCampaign[] = []
          statistics.map((statistic) => {
            const statisticCampaign: DWS.StatisticCampaign = {
              campaignId: statistic.campaignId,
              totalSent: statistic.totalSent,
              totalOpened: statistic.totalOpened,
              openedPercentage:  statistic.totalOpened ? Math.round(100 * statistic.totalOpened/statistic.totalSent): 0,
              lastOpenedAt: statistic.lastOpenedAt ? statistic.lastOpenedAt : '-',
              delivered: statistic.delivered,
              processed: statistic.processed,
              opens: statistic.opens,
              clicks: statistic.clicks,
              uniqueClicks: statistic.uniqueClicks,
              bounces: statistic.bounces,
              unsubscribes: statistic.unsubscribes,
            }
            data.push(statisticCampaign);
          });
          return data;
        })
      );
  }

  getSingleCampaign(campaignId: any, wineryId: string): Observable<DWS.StatisticCampaign> {
    let params: any = {
      reference: environment.values['dateSendGridStatistics'],
      wineryId: wineryId,
      id: campaignId,
      archived: true
    };
    return resource('crm://statistics/campaigns/id')
      .params(params)
      .get<DWS.StatisticCampaign[]>()
      .pipe(
        map((statistics) => {
          if (!statistics || !statistics.length) return {} as StatisticCampaign;
          const statistic = statistics[0];
          const data: DWS.StatisticCampaign = {
            campaignId: statistic.campaignId,
            totalSent: statistic.totalSent,
            totalOpened: statistic.totalOpened,
            openedPercentage:  statistic.totalOpened ? Math.round(100 * statistic.totalOpened/statistic.totalSent): 0,
            lastOpenedAt: statistic.lastOpenedAt ? statistic.lastOpenedAt : '-',
            totalBuys: statistic.totalBuys ? statistic.totalBuys : 0,
            totalIncome: statistic.totalIncome ? statistic.totalIncome / 100 : 0
          }
          return data;
        })
      );
  }

  getFullCampaignStatisticsById(campaignId: any): Observable<DWS.FullCampaignStatistic> {
    let params: any = {
      id: campaignId
    };
    return resource('crm://v2/statistics/campaigns/id')
      .params(params)
      .get<DWS.FullCampaignStatistic>()
      .pipe(
        map((statistics) => {
          if (!statistics) return {} as DWS.FullCampaignStatistic;
          return statistics as DWS.FullCampaignStatistic;
        })
      );
  }

  getFullCampaignStatistics(): Observable<DWS.FullCampaignStatistic> {
    return resource('crm://v2/statistics/campaigns')
      .get<DWS.FullCampaignStatistic>()
      .pipe(
        map((statistics) => {
          if (!statistics) return {} as DWS.FullCampaignStatistic;
          return statistics as DWS.FullCampaignStatistic;
        })
      );
  }

  getFullCampaignStatisticsByDate(campaignId: number): Observable<DWS.FullCampaignStatistic[]> {
    let params: any = {
      id: campaignId
    };
    return resource('crm://statistics/campaigns/id/summarybydate')
      .params(params)
      .get<DWS.FullCampaignStatistic[]>()
      .pipe(
        map((statistics) => {
          if (!statistics) return {} as DWS.FullCampaignStatistic[];
          return statistics as DWS.FullCampaignStatistic[];
        })
      );
  }

  getSendGridMessages(
    wineryId: string,
    categoryId: string,
    type: DownloadType,
    toEmail?: string,
    limit?: number,
    lastEventTimeBefore?: string,
    searchText?: string,
    cacheOptions?: { skipCacheRead?: boolean }
  ): Observable<SendGridMessagesPage> {
    const key = SendGridMessagesCacheService.buildKey(
      wineryId,
      categoryId,
      type,
      toEmail,
      limit,
      lastEventTimeBefore,
      searchText
    );
    return this.sendGridMessagesCache.runWithCache(
      key,
      this.getSendGridMessagesNetwork(
        wineryId,
        categoryId,
        type,
        toEmail,
        limit,
        lastEventTimeBefore,
        searchText
      ),
      cacheOptions
    );
  }

  private getSendGridMessagesNetwork(
    wineryId: string,
    categoryId: string,
    type: DownloadType,
    toEmail?: string,
    limit?: number,
    lastEventTimeBefore?: string,
    searchText?: string
  ): Observable<SendGridMessagesPage> {
    const params: any = { wineryId, categoryId };
    if (toEmail) params.toEmail = toEmail;
    if (limit != null) params.limit = limit;
    if (lastEventTimeBefore) params.lastEventTimeBefore = lastEventTimeBefore;
    if (searchText) params.searchText = searchText;

    const urlMap: Record<DownloadType, string> = {
      [DownloadType.ALL]: 'crm://email/download/all/wineryId',
      [DownloadType.NOT_DELIVERED]: 'crm://email/download/not-delivered/wineryId',
      [DownloadType.OPENED]: 'crm://email/download/opened/wineryId',
      [DownloadType.NOT_OPENED]: 'crm://email/download/not-opened/wineryId',
    };
    const resourceUrl = urlMap[type] || urlMap[DownloadType.ALL];

    return resource(resourceUrl)
      .headers({ 'x-max-age': '0' })
      .params(params)
      .get<SendGridMessagesPage>();
  }
}
